import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import { BarChart3, Search, Filter, PlayCircle, CheckCircle, AlertCircle, XCircle, User, Building2, FileText, Target, Zap, Trash2, RefreshCw, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { LineChart, Line, YAxis, ResponsiveContainer } from "recharts";
import { CVViewer } from "@/components/CVViewer";
import { AnalysisViewer } from "@/components/AnalysisViewer";
import { DECISION_COLORS } from "@/lib/analysis-constants";
import { convertScoreToFive } from "@/lib/utils";
import { analysesService, depotsService } from "@/services";
import api from "@/services/api";
import type { AnalysisSummaryPerDepot } from "@/types";

type AnalysisMode = 'anonymized_gemini' | 'infomaniak_dual';
type HardnessProfile = 'balanced' | 'strict_evidence' | 'very_strict';

const ANALYSIS_MODE_LABELS: Record<AnalysisMode, string> = {
  anonymized_gemini: 'Combo A: Vision + anonymisation Infomaniak -> scoring Gemini 3 Flash',
  infomaniak_dual: 'Combo B: Vision Infomaniak -> scoring dual Kimi K2.5 + Qwen3',
};
const HARDNESS_LABELS: Record<HardnessProfile, string> = {
  balanced: 'Équilibré',
  strict_evidence: 'Strict preuve',
  very_strict: 'Très strict',
};
interface Analyse {
  id: string;
  depot_id: string;
  ao_id: string;
  resultat_json: any;
  overall_score: number;
  decision: 'shortlist' | 'consider' | 'reject';
  rating?: string;
  comment?: string;
  is_excluded?: boolean;
  updated_at?: string;
  analysis_mode?: string;
  model_used?: string;
  tokens_in: number;
  tokens_out: number;
  cost_estimate: number;
  created_at: string;
  depots: {
    candidats: {
      nom: string;
      societes: {
        nom: string;
      };
    };
    appels_offres: {
      titre: string;
      reference: string;
    };
    texte_extrait: string;
    fichier_file_id: string;
  };
  // Dedup fields from latest-per-depot endpoint
  analysis_count?: number;
  avg_score?: number;
  score_history?: { score: number | null; createdAt: string; decision: string | null }[];
}
interface Depot {
  id: string;
  ao_id: string;
  texte_extrait: string;
  fichier_file_id?: string;
  candidats: {
    nom: string;
    societes: {
      nom: string;
    };
  };
  appels_offres: {
    titre: string;
    reference: string;
  };
}
const Analyses = () => {
  const [analyses, setAnalyses] = useState<Analyse[]>([]);
  const [depotsNonAnalyses, setDepotsNonAnalyses] = useState<Depot[]>([]);
  const [filteredAnalyses, setFilteredAnalyses] = useState<Analyse[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("all");
  const [aoFilter, setAoFilter] = useState("all");
  const [societeFilter, setSocieteFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState<string[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<Analyse | null>(null);
  const [cvPreviewOpen, setCvPreviewOpen] = useState(false);
  const [selectedCvData, setSelectedCvData] = useState<{
    candidatNom: string;
    societeNom: string;
    aoTitre: string;
    aoReference: string;
    texteExtrait: string;
    fichierFileId?: string;
    dateCreation?: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [analyseToDelete, setAnalyseToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteCvDialogOpen, setDeleteCvDialogOpen] = useState(false);
  const [cvToDelete, setCvToDelete] = useState<{
    id: string;
    fichierFileId?: string;
  } | null>(null);
  const [deletingCv, setDeletingCv] = useState(false);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [pendingAnalysisDepotId, setPendingAnalysisDepotId] = useState<string | null>(null);
  const [pendingAnalysisType, setPendingAnalysisType] = useState<'single' | 'batch' | 'reanalyze' | null>(null);
  const [depotExtractionCosts, setDepotExtractionCosts] = useState<Record<string, number | null>>({});
  const [selectedHardness, setSelectedHardness] = useState<HardnessProfile>('balanced');

  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    filterAnalyses();
  }, [analyses, searchTerm, decisionFilter, aoFilter, societeFilter]);
  const loadData = async () => {
    try {
      setLoading(true);
      const [analysesData, depotsData] = await Promise.all([
        analysesService.listLatestPerDepot(),
        depotsService.getAll(),
      ]);

      const transformedAnalyses: Analyse[] = (analysesData || []).map((item: any) => ({
        id: item.id,
        depot_id: item.depotId,
        ao_id: item.aoId,
        resultat_json: item.resultatJson,
        overall_score: item.overallScore || 0,
        decision: (item.decision || 'reject') as 'shortlist' | 'consider' | 'reject',
        rating: item.rating || undefined,
        comment: item.comment || undefined,
        is_excluded: item.isExcluded || false,
        updated_at: item.updatedAt || undefined,
        analysis_mode: item.analysisMode || undefined,
        model_used: item.modelUsed || undefined,
        tokens_in: item.tokensIn || 0,
        tokens_out: item.tokensOut || 0,
        cost_estimate: item.costEstimate || 0,
        created_at: item.createdAt,
        depots: {
          candidats: {
            nom: item.depot?.candidat?.nom || 'Inconnu',
            societes: {
              nom: item.depot?.candidat?.societe?.nom || 'Inconnue',
            },
          },
          appels_offres: {
            titre: item.depot?.appelOffre?.titre || '',
            reference: item.depot?.appelOffre?.reference || '',
          },
          texte_extrait: item.depot?.texteExtrait || '',
          fichier_file_id: item.depot?.fichierFileId || '',
        },
        analysis_count: item.analysisCount || 1,
        avg_score: item.avgScore ?? item.overallScore ?? 0,
        score_history: item.scoreHistory || [],
      }));

      const transformedDepots: Depot[] = (depotsData || []).map((depot: any) => ({
        id: depot.id,
        ao_id: depot.aoId,
        texte_extrait: depot.texteExtrait || '',
        fichier_file_id: depot.fichierFileId || undefined,
        candidats: {
          nom: depot.candidat?.nom || 'Inconnu',
          societes: {
            nom: depot.candidat?.societe?.nom || 'Inconnue',
          },
        },
        appels_offres: {
          titre: depot.appelOffre?.titre || '',
          reference: depot.appelOffre?.reference || '',
        },
      }));

      const analyzedDepotIds = new Set(transformedAnalyses.map(a => a.depot_id));
      const pendingDepots = transformedDepots.filter(d => !analyzedDepotIds.has(d.id));

      const extractionCostsMap: Record<string, number | null> = {};
      (depotsData || []).forEach((d: { id: string; extractionCost?: number | null }) => {
        extractionCostsMap[d.id] = d.extractionCost ?? null;
      });

      setAnalyses(transformedAnalyses);
      setDepotsNonAnalyses(pendingDepots);
      setDepotExtractionCosts(extractionCostsMap);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les analyses",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const filterAnalyses = () => {
    let filtered = analyses;

    // Filtre par texte
    if (searchTerm) {
      filtered = filtered.filter(analyse => analyse.depots.candidats.nom.toLowerCase().includes(searchTerm.toLowerCase()) || analyse.depots.candidats.societes.nom.toLowerCase().includes(searchTerm.toLowerCase()) || analyse.depots.appels_offres.titre.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    // Filtre par décision
    if (decisionFilter !== "all") {
      filtered = filtered.filter(analyse => analyse.decision === decisionFilter);
    }

    // Filtre par A.O.
    if (aoFilter !== "all") {
      filtered = filtered.filter(analyse => analyse.ao_id === aoFilter);
    }

    // Filtre par société
    if (societeFilter !== "all") {
      filtered = filtered.filter(analyse => analyse.depots.candidats.societes.nom === societeFilter);
    }
    setFilteredAnalyses(filtered);
  };
  const analyzeCV = async (
    depotId: string,
    options: {
      mode?: AnalysisMode;
      hardness?: HardnessProfile;
    } = {}
  ) => {
    if (analyzing.includes(depotId)) return;
    setAnalyzing(prev => [...prev, depotId]);
    const mode = options.mode || 'anonymized_gemini';
    const hardness = options.hardness || selectedHardness;
    try {
      const payload: Record<string, any> = {
        depotId,
        mode,
        hardness,
      };

      const { data: response } = await api.post('/analyses/analyze-cv', payload);

      const analysis = response?.data?.analysis;
      if (!analysis) {
        throw new Error('Analyse échouée');
      }

      const score = Number(analysis.overallScore || 0);
      const decision = analysis.decision || 'N/A';

      toast({
        title: "Analyse terminée",
        description: `Score: ${score}/100 (${convertScoreToFive(score)}/5) - ${decision} (${ANALYSIS_MODE_LABELS[mode]}, ${HARDNESS_LABELS[hardness]})`
      });

      // Recharger les données
      await loadData();
    } catch (error) {
      console.error('Erreur analyse:', error);
      const apiError = (error as any)?.response?.data?.error;
      const errorMessage = String(apiError || (error instanceof Error ? error.message : 'Erreur inconnue'));
      toast({
        title: errorMessage.includes('NO_CRITERIA_CONFIGURED') ? "Configuration requise" : "Analyse partielle",
        description: errorMessage.includes('NO_CRITERIA_CONFIGURED')
          ? "Aucun critère d'évaluation configuré. Configurez les critères avant de lancer l'analyse."
          : "L'analyse a rencontré des erreurs. Essayez de relancer l'analyse.",
        variant: "destructive"
      });
    } finally {
      setAnalyzing(prev => prev.filter(id => id !== depotId));
    }
  };
  const openModelSelector = (depotId: string, type: 'single' | 'batch' | 'reanalyze') => {
    setPendingAnalysisDepotId(depotId);
    setPendingAnalysisType(type);
    setModelSelectorOpen(true);
  };
  const handleModeSelection = async (
    mode: AnalysisMode,
  ) => {
    setModelSelectorOpen(false);
    if (pendingAnalysisType === 'single' && pendingAnalysisDepotId) {
      await analyzeCV(pendingAnalysisDepotId, {
        mode,
        hardness: selectedHardness,
      });
    } else if (pendingAnalysisType === 'reanalyze' && pendingAnalysisDepotId) {
      await reanalyzeCVWithMode(pendingAnalysisDepotId, {
        mode,
        hardness: selectedHardness,
      });
    } else if (pendingAnalysisType === 'batch') {
      await analyzeFilteredPendingWithMode(mode, selectedHardness);
    }
    setPendingAnalysisDepotId(null);
    setPendingAnalysisType(null);
  };
  // Fonction pour filtrer les dépôts non analysés selon l'AO sélectionné
  const getFilteredDepotsNonAnalyses = () => {
    if (aoFilter === "all") {
      return depotsNonAnalyses;
    }
    return depotsNonAnalyses.filter(depot => depot.ao_id === aoFilter);
  };
  const analyzeFilteredPending = async () => {
    const filteredDepots = getFilteredDepotsNonAnalyses();
    if (filteredDepots.length === 0) {
      toast({
        title: "Aucune analyse en attente",
        description: aoFilter === "all" ? "Tous les CV ont déjà été analysés" : "Aucun CV en attente pour cet appel d'offres",
        variant: "destructive"
      });
      return;
    }
    openModelSelector('batch', 'batch');
  };
  const analyzeFilteredPendingWithMode = async (
    mode: AnalysisMode,
    hardness: HardnessProfile,
  ) => {
    const filteredDepots = getFilteredDepotsNonAnalyses();
    const pendingIds = filteredDepots.map(d => d.id);
    toast({
      title: "Analyse en cours",
      description: `Traitement de ${pendingIds.length} CV avec ${ANALYSIS_MODE_LABELS[mode]}...`
    });

    // Analyser un par un pour éviter la surcharge
    for (const depotId of pendingIds) {
      await analyzeCV(depotId, {
        mode,
        hardness,
      });
      // Pause entre analyses
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };
  const getDecisionBadge = (decision: string, score: number) => {
    const formattedScore = Number(score).toFixed(1);
    const fiveScaleScore = convertScoreToFive(score);
    switch (decision) {
      case 'shortlist':
        return <Badge className="bg-success text-success-foreground">Shortlist ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'consider':
        return <Badge className="bg-warning text-warning-foreground">À considérer ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      case 'reject':
        return <Badge variant="destructive">Rejeté ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
      default:
        return <Badge variant="outline">Inconnu ({formattedScore}/100 • {fiveScaleScore}/5)</Badge>;
    }
  };
  const getDecisionIcon = (decision: string) => {
    switch (decision) {
      case 'shortlist':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'consider':
        return <AlertCircle className="h-5 w-5 text-warning" />;
      case 'reject':
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <BarChart3 className="h-5 w-5 text-muted-foreground" />;
    }
  };
  const openAnalysisDetail = (analyse: Analyse) => {
    setSelectedAnalysis(analyse);
  };
  const handleSelectAnalysis = async (analysisId: string) => {
    try {
      const data = await analysesService.getById(analysisId);
      const transformed: Analyse = {
        id: data.id,
        depot_id: data.depotId,
        ao_id: data.aoId,
        resultat_json: data.resultatJson,
        overall_score: data.overallScore || 0,
        decision: (data.decision || 'reject') as 'shortlist' | 'consider' | 'reject',
        rating: data.rating || undefined,
        comment: data.comment || undefined,
        is_excluded: data.isExcluded || false,
        updated_at: data.updatedAt || undefined,
        analysis_mode: data.analysisMode || undefined,
        model_used: data.modelUsed || undefined,
        tokens_in: data.tokensIn || 0,
        tokens_out: data.tokensOut || 0,
        cost_estimate: data.costEstimate || 0,
        created_at: data.createdAt,
        depots: {
          candidats: {
            nom: data.depot?.candidat?.nom || 'Inconnu',
            societes: {
              nom: data.depot?.candidat?.societe?.nom || 'Inconnue',
            },
          },
          appels_offres: {
            titre: data.depot?.appelOffre?.titre || '',
            reference: data.depot?.appelOffre?.reference || '',
          },
          texte_extrait: data.depot?.texteExtrait || '',
          fichier_file_id: data.depot?.fichierFileId || '',
        },
      };
      setSelectedAnalysis(transformed);
    } catch (error) {
      console.error('Erreur chargement analyse:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger cette analyse",
        variant: "destructive"
      });
    }
  };
  const openCvPreview = (analyse: Analyse) => {
    setSelectedCvData({
      candidatNom: analyse.depots.candidats.nom,
      societeNom: analyse.depots.candidats.societes.nom,
      aoTitre: analyse.depots.appels_offres.titre,
      aoReference: analyse.depots.appels_offres.reference,
      texteExtrait: analyse.depots.texte_extrait,
      fichierFileId: analyse.depots.fichier_file_id,
      dateCreation: analyse.created_at
    });
    setCvPreviewOpen(true);
  };
  const openCvPreviewForDepot = (depot: Depot) => {
    setSelectedCvData({
      candidatNom: depot.candidats.nom,
      societeNom: depot.candidats.societes.nom,
      aoTitre: depot.appels_offres.titre,
      aoReference: depot.appels_offres.reference,
      texteExtrait: depot.texte_extrait,
      fichierFileId: depot.fichier_file_id,
      dateCreation: undefined
    });
    setCvPreviewOpen(true);
  };
  const reanalyzeCV = async (depotId: string) => {
    openModelSelector(depotId, 'reanalyze');
  };
  const reanalyzeCVWithMode = async (
    depotId: string,
    options: {
      mode?: AnalysisMode;
      hardness?: HardnessProfile;
    } = {}
  ) => {
    if (analyzing.includes(depotId)) return;

    // Relancer l'analyse (on garde l'historique, pas de suppression)
    await analyzeCV(depotId, options);
  };
  const deleteAnalysis = async (analysisId: string) => {
    try {
      setDeleting(true);
      await analysesService.delete(analysisId);
      toast({
        title: "Analyse supprimée",
        description: "L'analyse a été supprimée avec succès"
      });

      // Recharger les données
      loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'analyse",
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setAnalyseToDelete(null);
    }
  };
  const openDeleteDialog = (analysisId: string) => {
    setAnalyseToDelete(analysisId);
    setDeleteDialogOpen(true);
  };
  const deleteCvDocument = async (depotId: string, _fichierFileId?: string) => {
    try {
      setDeletingCv(true);

      // Supprimer le dépôt (le backend gère la suppression des fichiers et analyses associés)
      await depotsService.delete(depotId);

      toast({
        title: "Document supprimé",
        description: "Le CV et ses analyses associées ont été supprimés avec succès"
      });

      // Recharger les données
      loadData();
    } catch (error) {
      console.error('Erreur lors de la suppression du CV:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive"
      });
    } finally {
      setDeletingCv(false);
      setDeleteCvDialogOpen(false);
      setCvToDelete(null);
    }
  };
  const openDeleteCvDialog = (depotId: string, fichierFileId?: string) => {
    setCvToDelete({
      id: depotId,
      fichierFileId
    });
    setDeleteCvDialogOpen(true);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir la liste unique des A.O. pour le filtre
  const uniqueAOMap = new Map<string, { id: string; titre: string; reference: string }>();
  analyses.forEach(a => {
    if (!uniqueAOMap.has(a.ao_id)) {
      uniqueAOMap.set(a.ao_id, {
        id: a.ao_id,
        titre: a.depots.appels_offres.titre || '',
        reference: a.depots.appels_offres.reference || '',
      });
    }
  });
  depotsNonAnalyses.forEach(d => {
    if (!uniqueAOMap.has(d.ao_id)) {
      uniqueAOMap.set(d.ao_id, {
        id: d.ao_id,
        titre: d.appels_offres.titre || '',
        reference: d.appels_offres.reference || '',
      });
    }
  });
  const uniqueAOs = Array.from(uniqueAOMap.values());

  // Obtenir la liste unique des sociétés pour le filtre (en fonction de l'AO sélectionné)
  const uniqueSocietes = Array.from(new Set(analyses.filter(a => aoFilter === "all" || a.ao_id === aoFilter).map(a => a.depots.candidats.societes.nom))).filter(Boolean).sort();
  if (loading) {
    return <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des analyses...</p>
          </div>
        </div>
      </Layout>;
  }
  return <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Analyses IA</h1>
            <p className="text-muted-foreground">Résultats du scoring automatisé des Dossiers</p>
          </div>
          <div className="flex space-x-2">
            <Button variant="destructive" onClick={() => navigate('/import-cv')} className="bg-[_#2878f0] bg-[#2878f0]">
              <FileText className="h-4 w-4 mr-2" />
              Importer documents
            </Button>
            <Button variant="destructive" onClick={analyzeFilteredPending} disabled={getFilteredDepotsNonAnalyses().length === 0 || analyzing.length > 0} className="bg-[#2878f0]">
              <Zap className="h-4 w-4 mr-2" />
              Analyser ({getFilteredDepotsNonAnalyses().length})
            </Button>
          </div>
        </div>

        {/* Filtres */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              Filtres
            </CardTitle>
            <CardDescription>Filtrez les analyses par dossiers et société</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Analyses</label>
                <Select value={aoFilter} onValueChange={setAoFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tous les AO" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les analyses</SelectItem>
                    {uniqueAOs.map(ao => <SelectItem key={ao.id} value={ao.id}>
                        {ao.reference} - {ao.titre}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Société</label>
                <Select value={societeFilter} onValueChange={setSocieteFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Toutes les sociétés" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les sociétés</SelectItem>
                    {uniqueSocietes.map(societe => <SelectItem key={societe} value={societe}>
                        {societe}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents en attente d'analyse */}
        {depotsNonAnalyses.length > 0 && <Card className="card-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <PlayCircle className="h-5 w-5 mr-2" />
                Documents en attente d&apos;analyse ({depotsNonAnalyses.length})
              </CardTitle>
              <CardDescription>
                Lancez l'analyse IA pour ces candidatures
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {depotsNonAnalyses.slice(0, 5).map(depot => <div key={depot.id} className="flex items-center justify-between p-3 border border-card-border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <h4 className="font-medium">{depot.candidats.nom}</h4>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Building2 className="h-3 w-3 mr-1" />
                          {depot.candidats.societes.nom}
                          <span className="mx-2">•</span>
                          <Target className="h-3 w-3 mr-1" />
                          {depot.appels_offres.titre}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openCvPreviewForDepot(depot)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDeleteCvDialog(depot.id)} className="text-destructive hover:text-destructive" title="Supprimer le document">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => openModelSelector(depot.id, 'single')} disabled={analyzing.includes(depot.id)}>
                        {analyzing.includes(depot.id) ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" /> : <PlayCircle className="h-4 w-4 mr-2" />}
                        {analyzing.includes(depot.id) ? 'Analyse...' : 'Analyser'}
                      </Button>
                    </div>
                  </div>)}
                {depotsNonAnalyses.length > 5 && <p className="text-sm text-muted-foreground text-center">
                    Et {depotsNonAnalyses.length - 5} autres...
                  </p>}
              </div>
            </CardContent>
          </Card>}

        {/* Filtres */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Rechercher par candidat, société ou poste..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8" />
                </div>
              </div>
              <Select value={decisionFilter} onValueChange={setDecisionFilter}>
                <SelectTrigger className="w-full sm:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Décision" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les décisions</SelectItem>
                  <SelectItem value="shortlist">Shortlist</SelectItem>
                  <SelectItem value="consider">À considérer</SelectItem>
                  <SelectItem value="reject">Rejetés</SelectItem>
                </SelectContent>
              </Select>
              <Select value={aoFilter} onValueChange={value => {
              setAoFilter(value);
              // Réinitialiser le filtre société quand l'AO change
              setSocieteFilter("all");
            }}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Appel d'offres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les A.O.</SelectItem>
                  {uniqueAOs.map(ao => <SelectItem key={ao.id} value={ao.id}>
                      {ao.titre} ({ao.reference})
                    </SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={societeFilter} onValueChange={setSocieteFilter}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder="Société" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les sociétés</SelectItem>
                  {uniqueSocietes.map(societe => <SelectItem key={societe} value={societe}>
                      {societe}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Liste des analyses */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>
              Candidats analysés ({filteredAnalyses.length})
            </CardTitle>
            <CardDescription>
              Dernière analyse par candidat — cliquez pour voir le détail et l'historique
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredAnalyses.length === 0 ? <div className="text-center py-12">
                <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {analyses.length === 0 ? "Aucune analyse" : "Aucun résultat"}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {analyses.length === 0 ? "Commencez par importer et analyser des documents" : "Essayez d'ajuster vos filtres de recherche"}
                </p>
                {analyses.length === 0 && <Button onClick={() => navigate('/import-cv')}>
                    <FileText className="h-4 w-4 mr-2" />
                    Importer des documents
                  </Button>}
              </div> : <div className="space-y-4">
                {filteredAnalyses.map(analyse => {
                  const hasMultipleRuns = (analyse.analysis_count ?? 1) > 1;
                  const sparklineData = (analyse.score_history || []).map((h, i) => ({
                    name: `#${i + 1}`,
                    score: h.score ?? 0,
                    decision: h.decision,
                  }));

                  return <div key={analyse.id} className="flex items-center justify-between p-4 border border-card-border rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer" onClick={() => openAnalysisDetail(analyse)}>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          {getDecisionIcon(analyse.decision)}
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-lg">
                                {analyse.depots.fichier_file_id?.split('-').slice(1).join('-') || analyse.depots.candidats.nom}
                              </h4>
                              {hasMultipleRuns && (
                                <Badge variant="secondary" className="text-xs px-2 py-0 h-5">
                                  {analyse.analysis_count} runs
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center text-sm text-muted-foreground mt-1">
                              <Building2 className="h-3 w-3 mr-1" />
                              {analyse.depots.candidats.societes.nom}
                              <span className="mx-2">•</span>
                              <Target className="h-3 w-3 mr-1" />
                              {analyse.depots.appels_offres.titre}
                            </div>
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                              <span>Analysé le {formatDate(analyse.created_at)}</span>
                              {analyse.resultat_json?.analysis_metadata?.model_used && (
                                <Badge variant="outline" className="text-xs px-2 py-0 h-5">
                                  {analyse.resultat_json.analysis_metadata.mode || analyse.resultat_json.analysis_metadata.model_used}
                                </Badge>
                              )}
                              <span>Tokens: {analyse.tokens_in + analyse.tokens_out}</span>
                              <span>Coût: CHF {analyse.cost_estimate.toFixed(4)}</span>
                            </div>
                          </div>
                        </div>
                         <div className="flex items-center space-x-4">
                           {/* Mini sparkline for multi-run candidates */}
                           {hasMultipleRuns && sparklineData.length > 1 && (
                             <div className="w-[60px] h-[32px]">
                               <ResponsiveContainer width="100%" height="100%">
                                 <LineChart data={sparklineData}>
                                   <YAxis domain={[0, 100]} hide />
                                   <Line
                                     type="monotone"
                                     dataKey="score"
                                     stroke="#6366f1"
                                     strokeWidth={1.5}
                                     dot={({ cx, cy, payload }: any) => (
                                       <circle
                                         key={`dot-${payload.name}`}
                                         cx={cx}
                                         cy={cy}
                                         r={2.5}
                                         fill={DECISION_COLORS[payload.decision] || "#888"}
                                         stroke="white"
                                         strokeWidth={1}
                                       />
                                     )}
                                   />
                                 </LineChart>
                               </ResponsiveContainer>
                             </div>
                           )}
                           <div className="text-right space-y-1">
                             <div className="text-2xl font-bold">
                               {analyse.overall_score}/100
                             </div>
                             {hasMultipleRuns && (
                               <div className="text-xs text-muted-foreground">
                                 Moy. {analyse.avg_score?.toFixed(1)}/100
                               </div>
                             )}
                             <div className="text-xs font-medium text-muted-foreground">
                               Note {convertScoreToFive(analyse.overall_score)}/5
                             </div>
                             <Progress value={analyse.overall_score} className="w-24 h-2" />
                           </div>
                           {getDecisionBadge(analyse.decision, analyse.overall_score)}
                            <div className="flex items-center space-x-2" onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={() => reanalyzeCV(analyse.depot_id)} disabled={analyzing.includes(analyse.depot_id)} title="Relancer l'analyse">
                                {analyzing.includes(analyse.depot_id) ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" /> : <RefreshCw className="h-4 w-4" />}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openDeleteCvDialog(analyse.depot_id, analyse.depots.fichier_file_id)} className="text-destructive hover:text-destructive" title="Supprimer le document">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(analyse.id)} className="text-destructive hover:text-destructive" title="Supprimer l'analyse seulement">
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                         </div>
                      </div>
                    </div>
                  </div>;
                })}
              </div>}
          </CardContent>
        </Card>
      </div>

      {/* Analysis Viewer */}
      <AnalysisViewer
        isOpen={!!selectedAnalysis}
        onClose={() => setSelectedAnalysis(null)}
        analysis={selectedAnalysis}
        onViewCV={openCvPreview}
        onReanalyze={depotId => {
          setSelectedAnalysis(null);
          reanalyzeCV(depotId);
        }}
        onDelete={analysisId => {
          setSelectedAnalysis(null);
          openDeleteDialog(analysisId);
        }}
        onSelectAnalysis={handleSelectAnalysis}
        onUpdateAnalysis={loadData}
        isReanalyzing={selectedAnalysis ? analyzing.includes(selectedAnalysis.depot_id) : false}
        depotExtractionCost={selectedAnalysis ? depotExtractionCosts[selectedAnalysis.depot_id] ?? null : null}
        isFirstRun={selectedAnalysis ? (() => {
          const depotAnalyses = analyses.filter(a => a.depot_id === selectedAnalysis.depot_id)
            .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          return depotAnalyses[0]?.id === selectedAnalysis.id;
        })() : false}
      />

      {/* Sélecteur de modèle */}
      <Dialog open={modelSelectorOpen} onOpenChange={setModelSelectorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Choisir le mode d'analyse</DialogTitle>
            <DialogDescription>
              Sélectionnez le combo d'analyse à appliquer aux CV choisis.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-sm font-medium">Niveau de sévérité</div>
              <Select value={selectedHardness} onValueChange={(value: HardnessProfile) => setSelectedHardness(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir la sévérité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="balanced">Équilibré</SelectItem>
                  <SelectItem value="strict_evidence">Strict preuve</SelectItem>
                  <SelectItem value="very_strict">Très strict</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Ce paramètre influence la prudence du scoring et les guardrails appliqués au run.
              </p>
            </div>

            <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => handleModeSelection('anonymized_gemini')}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="h-3 w-3 rounded-full bg-primary"></div>
                  <div className="flex-1">
                    <h3 className="font-medium">Combo A: Infomaniak + Gemini 3 Flash</h3>
                    <p className="text-sm text-muted-foreground">Extraction Vision et anonymisation via Infomaniak, puis scoring avec Gemini 3 Flash.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => handleModeSelection('infomaniak_dual')}>
              <CardContent className="p-4">
                <div className="flex items-center space-x-3">
                  <div className="h-3 w-3 rounded-full bg-emerald-500"></div>
                  <div className="flex-1">
                    <h3 className="font-medium">Combo B: Dual Infomaniak (Kimi K2.5 + Qwen3)</h3>
                    <p className="text-sm text-muted-foreground">Extraction Vision Infomaniak, puis scoring parallèle Kimi K2.5 + Qwen3 avec consensus.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setModelSelectorOpen(false)}>
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CV Viewer */}
      {selectedCvData && <CVViewer isOpen={cvPreviewOpen} onClose={() => {
      setCvPreviewOpen(false);
      setSelectedCvData(null);
    }} cvData={selectedCvData} />}

      {/* Dialog de confirmation de suppression d'analyse */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette analyse ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => analyseToDelete && deleteAnalysis(analyseToDelete)} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive-foreground mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
              Supprimer l'analyse
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmation de suppression de CV */}
      <AlertDialog open={deleteCvDialogOpen} onOpenChange={setDeleteCvDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression du document</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce document CV ? Cette action supprimera le fichier et toutes les analyses associées. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => cvToDelete && deleteCvDocument(cvToDelete.id, cvToDelete.fichierFileId)} disabled={deletingCv} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deletingCv ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive-foreground mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Supprimer le document
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>;
};
export default Analyses;
